import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { formatMoney } from "@/lib/format";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { ArrowLeft, Save, Loader2, History, Sparkles } from "lucide-react";
import toast from "react-hot-toast";

import {
  TemplateBasicInfoForm,
  TemplateEditorPanel,
  TemplatePreviewPanel,
  VersionHistoryModal,
  AIGeneratorModal,
} from "@/components/templates";
import type { Template, VariableInfo } from "@/components/templates";

export default function TemplateEditPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === "new";

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"email" | "pdf">("email");
  const [category, setCategory] = useState("newsletter");
  const [subject, setSubject] = useState("");
  const [subjectSk, setSubjectSk] = useState("");
  const [content, setContent] = useState("");
  const [contentSk, setContentSk] = useState("");
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [editorMode, setEditorMode] = useState<"wysiwyg" | "html">("wysiwyg");
  const [language, setLanguage] = useState<"cs" | "sk">("cs");
  const [showVariables, setShowVariables] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  // previewHtml is now computed via useMemo below

  // Fetch template
  const { data: templateData, isLoading } = useQuery({
    queryKey: ["template", id],
    queryFn: () => api.get<ApiResponse<Template>>(`/api/admin/templates/${id}`),
    enabled: !isNew,
  });

  // Fetch variables
  const { data: variablesData } = useQuery({
    queryKey: ["template-variables"],
    queryFn: () =>
      api.get<ApiResponse<Record<string, VariableInfo[]>>>("/api/admin/templates/variables"),
  });

  const variables = variablesData?.data || {};

  // Initialize form from template
  const lastSyncedTemplateId = useRef<string | null>(null);
  useEffect(() => {
    if (templateData?.data && templateData.data.id !== lastSyncedTemplateId.current) {
      lastSyncedTemplateId.current = templateData.data.id;
      const t = templateData.data;
      setName(t.name);
      setDescription(t.description || "");
      setType(t.type);
      setCategory(t.category);
      setSubject(t.subject || "");
      setSubjectSk(t.subjectSk || "");
      setContent(t.content);
      setContentSk(t.contentSk || "");
      setIsActive(t.isActive);
    }
  }, [templateData?.data]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      Placeholder.configure({ placeholder: t("opsAdmin.templates.contentPlaceholder") }),
    ],
    content: language === "sk" ? contentSk : content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (language === "sk") {
        setContentSk(html);
      } else {
        setContent(html);
      }
    },
  });

  // Update editor when language changes
  useEffect(() => {
    if (editor) {
      const newContent = language === "sk" ? contentSk : content;
      if (editor.getHTML() !== newContent) {
        editor.commands.setContent(newContent || "");
      }
    }
  }, [language, editor, content, contentSk]);

  // Compute preview HTML from content with sample variable replacements
  const previewHtml = useMemo(() => {
    if (isNew || !id) return "";
    const currentContent = language === "sk" ? contentSk : content;
    let preview = currentContent;
    const testData: Record<string, string> = {
      firstName: "John",
      lastName: "Smith",
      tripName: "Discover the beauty of Vietnam",
      destination: "Vietnam",
      price: formatMoney(32900),
      orderNumber: "ORD-2025-0042",
      companyName: "GO2 Vietnam",
    };
    for (const [key, value] of Object.entries(testData)) {
      preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
    }
    return preview;
  }, [content, contentSk, language, id, isNew]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Template>) =>
      isNew
        ? api.post<ApiResponse<Template>>("/api/admin/templates", data)
        : api.put<ApiResponse<Template>>(`/api/admin/templates/${id}`, data),
    onSuccess: (data) => {
      toast.success(
        isNew ? t("opsAdmin.templates.toastCreated") : t("opsAdmin.templates.toastSaved"),
      );
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (isNew && data.data?.id) {
        navigate(`/templates/${data.data.id}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t("opsAdmin.templates.toastSaveFailed"));
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error(t("opsAdmin.templates.errNameRequired"));
      return;
    }
    if (!content.trim()) {
      toast.error(t("opsAdmin.templates.errContentRequired"));
      return;
    }

    saveMutation.mutate({
      name,
      description: description || undefined,
      type,
      category,
      subject: type === "email" ? subject : undefined,
      subjectSk: type === "email" ? subjectSk : undefined,
      content,
      contentSk: contentSk || undefined,
      isActive,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/templates")} className="btn-ghost">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? t("opsAdmin.templates.new") : name || t("opsAdmin.templates.editTitle")}
            </h1>
            {!isNew && templateData?.data?.versionCount && (
              <p className="text-sm text-gray-500">
                {t("opsAdmin.templates.versionCount", {
                  version: templateData.data.versionCount,
                })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isNew && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                className="btn-ghost"
                title={t("opsAdmin.templates.versionHistory")}
              >
                <History className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowAiModal(true)}
                className="btn-ghost"
                title={t("opsAdmin.templates.aiGeneration")}
              >
                <Sparkles className="h-5 w-5" />
              </button>
            </>
          )}
          <button onClick={handleSave} disabled={saveMutation.isPending} className="btn-primary">
            {saveMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            {t("common.actions.save")}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left panel - Editor */}
        <div className="space-y-4">
          <TemplateBasicInfoForm
            name={name}
            description={description}
            type={type}
            category={category}
            subject={subject}
            subjectSk={subjectSk}
            isActive={isActive}
            isNew={isNew}
            language={language}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onTypeChange={setType}
            onCategoryChange={setCategory}
            onSubjectChange={setSubject}
            onSubjectSkChange={setSubjectSk}
            onIsActiveChange={setIsActive}
          />

          <TemplateEditorPanel
            editor={editor}
            editorMode={editorMode}
            language={language}
            content={content}
            contentSk={contentSk}
            showVariables={showVariables}
            variables={variables}
            onEditorModeChange={setEditorMode}
            onLanguageChange={setLanguage}
            onContentChange={setContent}
            onContentSkChange={setContentSk}
            onShowVariablesChange={setShowVariables}
          />
        </div>

        {/* Right panel - Preview */}
        <div className="space-y-4">
          <TemplatePreviewPanel language={language} previewHtml={previewHtml} />
        </div>
      </div>

      {/* Version History Modal */}
      {showHistory && !isNew && (
        <VersionHistoryModal
          templateId={id!}
          onClose={() => setShowHistory(false)}
          onRestore={() => {
            queryClient.invalidateQueries({ queryKey: ["template", id] });
            setShowHistory(false);
          }}
        />
      )}

      {/* AI Generator Modal */}
      {showAiModal && (
        <AIGeneratorModal
          type={type}
          category={category}
          onClose={() => setShowAiModal(false)}
          onUse={(generated) => {
            if (generated.subject) {
              setSubject(generated.subject);
            }
            setContent(generated.content);
            if (editor) {
              editor.commands.setContent(generated.content);
            }
            setShowAiModal(false);
          }}
        />
      )}
    </div>
  );
}
